/**
 * The detail dialog of one supplier document (batch 8 T12c3, #3033): the STORED status and revision, the audit events (actors as the
 * server named them, never invented), the stored evidence and the LIVE review (the matcher run again against the facts as they are
 * now), and the two decisions: accept a variance and reject.
 *
 * Accepting quotes the revision and the evidence hash of the live review the user is looking at, with a reason and an explicit
 * override confirmation; it is offered only to a session that holds the override right, and never for a document whose own
 * arithmetic is wrong (the server refuses that whatever the grants; the screen says so instead of offering it). Rejecting quotes the
 * revision and a reason and is final. Both are journaled commands. After `stale_revision` / `stale_evidence` nothing is retried and no
 * approval is carried over: both decisions stay closed until the user explicitly refreshes the review.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  useInvoiceDetail,
  useInvoiceFacts,
  useInvoiceOperations,
  errorReason,
  type InvoiceDetailResponse,
  type InvoiceOperations,
  type InvoiceReview,
  type OperationResult,
} from '@/hooks/useSupplierInvoices';
import type { JournalOperation } from './invoiceJournal';
import { decisionReasonError } from './invoiceDraft';
import { InvoiceBoundary, useStillShown } from './InvoiceBoundary';
import { CoverageSummary, EvaluationView_, PendingOperations, Section } from './InvoiceParts';
import { useInvoiceAccess } from './invoiceSession';
import { LineStatusChip, NS, StatusChip, ViolationList, isStaleRefusal, poLineLabel, useErrorText, useFormatters, type ErrorText } from './invoiceView';

export interface SupplierInvoiceDetailDialogProps {
  open: boolean;
  invoiceId: string;
  /** The order the document belongs to, when the caller knows it (lets an unresolved decision be found before the detail loads). */
  purchaseOrderId?: string;
  onClose: () => void;
  /** Called after a decision was stored. */
  onChanged?: () => void;
}

/** Standalone: enforces the owner, rights and target boundary itself. */
export function SupplierInvoiceDetailDialog(props: SupplierInvoiceDetailDialogProps) {
  return (
    <InvoiceBoundary target={props.invoiceId}>
      <DetailDialog {...props} />
    </InvoiceBoundary>
  );
}

interface Captured {
  readonly revision: number;
  readonly evidenceHash: string;
}

type Decision = 'accept' | 'reject';

function DetailDialog({ open, invoiceId, purchaseOrderId, onClose, onChanged }: SupplierInvoiceDetailDialogProps) {
  const { t } = useTranslation(NS);
  const { t: tc } = useTranslation('common');
  const access = useInvoiceAccess();
  const stillShown = useStillShown();
  const detail = useInvoiceDetail(invoiceId);
  const orderId = detail.data?.invoice.purchaseOrderId ?? purchaseOrderId ?? '';
  const facts = useInvoiceFacts(orderId);
  const allOps = useInvoiceOperations(orderId, stillShown);
  const errorText = useErrorText();
  const fmt = useFormatters();

  const [deciding, setDeciding] = useState<{ kind: Decision; captured: Captured } | null>(null);
  const [staleNotice, setStaleNotice] = useState(false);
  const [done, setDone] = useState<{ kind: Decision; revision: number } | null>(null);
  /** The server refused the explicit retry of a held decision: the held request is gone, nothing was stored. */
  const [retryRefusal, setRetryRefusal] = useState<ErrorText | null>(null);
  /** Revisions already reported to the caller: a stored decision is reported once, whichever way its answer came. */
  const reported = useRef(new Set<string>());

  // Only this document's decisions are of interest here.
  const ops: InvoiceOperations = { ...allOps, operations: allOps.operations.filter((operation) => operation.target === invoiceId && operation.kind !== 'create') };
  const decisionPending = ops.operations.length > 0;

  const data = detail.data;
  const invoice = data?.invoice;
  const review = data?.review ?? null;
  const poLines = facts.data?.lines ?? [];
  const lineById = new Map(poLines.map((line) => [line.id.toLowerCase(), line] as const));

  const arithmeticWrong = review !== null && review.ok && !review.result.arithmetic.ok;
  const reviewStillVariance = review !== null && review.ok && review.result.proposedStatus === 'variance';
  const canAccept = access.canOverride && invoice?.status === 'variance' && review !== null && review.ok && reviewStillVariance && !arithmeticWrong && !decisionPending && !staleNotice;
  const canReject =
    invoice !== undefined &&
    invoice.status !== 'rejected' &&
    (invoice.status === 'accepted' ? access.canOverride : access.canMatch) &&
    !decisionPending &&
    !staleNotice;

  const open_ = (kind: Decision) => {
    if (!invoice) return;
    const evidenceHash = review !== null && review.ok ? review.evidenceHash : '';
    setDeciding({ kind, captured: { revision: invoice.revision, evidenceHash } });
  };

  const submit = async (kind: Decision, captured: Captured, reason: string): Promise<OperationResult> => {
    if (!invoice) return { status: 'canceled' };
    setRetryRefusal(null);
    // The same rule the server applies (trimmed, not empty, at most 500, no control character): nothing invalid is journaled or sent.
    if (decisionReasonError(reason) !== null) return { status: 'blocked', reason: 'invalid' };
    const result =
      kind === 'accept'
        ? await ops.accept({ id: invoice.id, invoiceNumber: invoice.invoiceNumber }, { reason, evidenceHash: captured.evidenceHash, expectedRevision: captured.revision })
        : await ops.reject({ id: invoice.id, invoiceNumber: invoice.invoiceNumber, accepted: invoice.status === 'accepted' }, { reason, expectedRevision: captured.revision });
    if (!stillShown()) return result;
    if (result.status === 'success') finishDecision(kind, result.result.revision);
    else if (result.status === 'refused' && isStaleRefusal(result.error)) showStale();
    return result;
  };

  /** The one place a stored decision is reported: the first-try answer and the answer of the pending notice's retry both end here. */
  const finishDecision = (kind: Decision, revision: number) => {
    setDeciding(null);
    setDone({ kind, revision });
    setStaleNotice(false);
    setRetryRefusal(null);
    const id = `${kind}:${String(revision)}`;
    if (reported.current.has(id)) return;
    reported.current.add(id);
    onChanged?.();
  };

  // Nothing was stored and nothing is retried: a fresh review is the only way on.
  const showStale = () => {
    setDeciding(null);
    setStaleNotice(true);
    setDone(null);
  };

  /**
   * The answer of an explicit retry from the pending notice. Only a decision of THIS document counts; `success` only arrives while
   * the owner, rights and document still hold. Matches what the first try would have done: stored -> done, closed, caller told;
   * stale -> fresh review needed; any other refusal -> shown, and the open decision may be confirmed again with a new request.
   */
  const onRetryResult = (result: OperationResult, operation: JournalOperation) => {
    if (!stillShown() || operation.kind === 'create' || operation.target !== invoiceId) return;
    if (result.status === 'success') finishDecision(operation.kind, result.result.revision);
    else if (result.status === 'refused') {
      if (isStaleRefusal(result.error)) showStale();
      else setRetryRefusal(errorText(result.error));
    }
  };

  const orderNumber = data?.purchaseOrder.orderNumber ?? null;

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth mobileTitle={t('detail.title')} aria-labelledby="supplier-invoice-detail-title">
      <DialogTitle id="supplier-invoice-detail-title">
        {invoice ? t(`form.kind.${invoice.documentKind}`) : t('detail.title')}
        {invoice && (
          <Typography component="span" variant="body1" sx={{ ml: 1 }}>
            {invoice.invoiceNumber}
          </Typography>
        )}
        {orderNumber !== null && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {t('entry.forOrder', { number: orderNumber })}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {!access.canRead ? (
          <Alert severity="info">{t('access.noRead')}</Alert>
        ) : (
          <Stack spacing={2}>
            {detail.isLoading && (
              <Stack direction="row" spacing={1} alignItems="center" role="status">
                <CircularProgress size={18} />
                <Typography variant="body2">{t('loading.detail')}</Typography>
              </Stack>
            )}
            {detail.isError && (
              <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => detail.refetch()}>{t('retryRead')}</Button>}>
                {errorText(detail.error).message}
              </Alert>
            )}
            <PendingOperations ops={ops} onResult={onRetryResult} />
            {staleNotice && (
              <Alert
                severity="warning"
                data-testid="stale-notice"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setStaleNotice(false);
                      detail.refetch();
                    }}
                  >
                    {t('decision.refreshReview')}
                  </Button>
                }
              >
                <AlertTitle>{t('decision.staleTitle')}</AlertTitle>
                {t('decision.staleBody')}
              </Alert>
            )}
            {done && (
              <Alert severity="success" role="status" data-testid="decision-done">
                {t(`decision.done.${done.kind}`, { revision: done.revision })} {t('evaluation.noStockEffect')}
              </Alert>
            )}
            {data && invoice && (
              <>
                <DocumentHeader data={data} />
                {invoice.status === 'rejected' && <Alert severity="info">{t('detail.rejectedNote')}</Alert>}
                <Section title={t('detail.linesTitle')}>
                  <TableContainer>
                    <Table size="small" aria-label={t('detail.linesAria')}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('evaluation.columns.line')}</TableCell>
                          <TableCell>{t('evaluation.columns.poLine')}</TableCell>
                          <TableCell align="right">{t('evaluation.columns.quantity')}</TableCell>
                          <TableCell align="right">{t('detail.columns.unitPrice')}</TableCell>
                          <TableCell align="right">{t('detail.columns.vat')}</TableCell>
                          <TableCell align="right">{t('detail.columns.calculatedNet')}</TableCell>
                          <TableCell align="right">{t('detail.columns.statedNet')}</TableCell>
                          <TableCell>{t('evaluation.columns.status')}</TableCell>
                          <TableCell align="right">{t('evaluation.columns.variance')}</TableCell>
                          <TableCell>{t('detail.columns.links')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoice.lines.map((line) => (
                          <TableRow key={line.id} data-testid={`detail-line-${String(line.lineNumber)}`}>
                            <TableCell>{line.lineNumber}</TableCell>
                            <TableCell>
                              <div>{line.description}</div>
                              <Typography variant="caption" color="text.secondary">
                                {poLineLabel(line.purchaseOrderLineId ? lineById.get(line.purchaseOrderLineId.toLowerCase()) : undefined, t)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{line.quantity}</TableCell>
                            <TableCell align="right">
                              {fmt.unit(line.unitPriceNetCents)}
                              {line.discountPercent !== 0 && <div>{t('detail.discount', { percent: fmt.percent(line.discountPercent) })}</div>}
                            </TableCell>
                            <TableCell align="right">{fmt.percent(line.vatPercent)}</TableCell>
                            <TableCell align="right">{fmt.cents(line.lineNetCents)}</TableCell>
                            <TableCell align="right">{line.statedLineNetCents === null ? t('detail.notStated') : fmt.cents(line.statedLineNetCents)}</TableCell>
                            <TableCell>
                              <LineStatusChip status={line.matchStatus} />
                            </TableCell>
                            <TableCell align="right">
                              {line.varianceCents !== null && <div>{fmt.cents(line.varianceCents)}</div>}
                              {line.varianceQuantity !== null && line.varianceQuantity !== 0 && <div>{t('evaluation.varianceQuantity', { count: line.varianceQuantity })}</div>}
                            </TableCell>
                            <TableCell>
                              {line.receiptAllocations.map((allocation) => (
                                <div key={allocation.id}>{t('detail.allocation', { receipt: allocation.receiptNumber ?? t('form.unnumbered'), quantity: allocation.quantity })}</div>
                              ))}
                              {line.creditLinks.map((link) => (
                                <div key={link.id}>{t('detail.creditLink', { number: link.returnNumber ?? t('form.unnumbered'), quantity: link.quantity })}</div>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Section>
                <Section title={t('detail.reviewTitle')}>
                  <LiveReview review={review} poLines={poLines} status={invoice.status} />
                </Section>
                <Section title={t('detail.coverageTitle')}>
                  <CoverageSummary coverage={data.coverage} poLines={poLines} />
                </Section>
                <Section title={t('detail.auditTitle')}>
                  <AuditTable data={data} />
                </Section>
                {arithmeticWrong && invoice.status === 'variance' && (
                  <Alert severity="error" data-testid="arithmetic-not-acceptable">
                    {t('decision.arithmeticCannotBeAccepted')}
                  </Alert>
                )}
                {invoice.status === 'variance' && !access.canOverride && <Alert severity="info">{t('decision.needsOverride')}</Alert>}
                {invoice.status === 'accepted' && !access.canOverride && <Alert severity="info">{t('decision.rejectAcceptedNeedsOverride')}</Alert>}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {canAccept && (
          <Button variant="contained" color="warning" onClick={() => open_('accept')}>
            {t('decision.accept')}
          </Button>
        )}
        {canReject && (
          <Button variant="outlined" color="error" onClick={() => open_('reject')}>
            {t('decision.reject')}
          </Button>
        )}
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
      {deciding && invoice && (
        <DecisionDialog
          kind={deciding.kind}
          captured={deciding.captured}
          live={{ revision: invoice.revision, evidenceHash: review !== null && review.ok ? review.evidenceHash : '' }}
          review={review}
          pending={decisionPending}
          retryRefusal={retryRefusal}
          invoiceNumber={invoice.invoiceNumber}
          onClose={() => setDeciding(null)}
          onSubmit={(reason) => submit(deciding.kind, deciding.captured, reason)}
        />
      )}
    </ResponsiveDialog>
  );
}

function DocumentHeader({ data }: { data: InvoiceDetailResponse }) {
  const { t } = useTranslation(NS);
  const fmt = useFormatters();
  const { invoice } = data;
  return (
    <Box data-testid="document-header">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <StatusChip status={invoice.status} size="medium" />
        <Chip size="small" variant="outlined" label={t('detail.revision', { revision: invoice.revision })} />
        <Chip size="small" variant="outlined" label={t('evaluation.noStockEffect')} />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
        <Typography variant="body2">{t('detail.invoiceDate', { date: fmt.date(invoice.invoiceDate) })}</Typography>
        <Typography variant="body2">{t('detail.createdBy', { name: invoice.createdBy.name || t('audit.unknownActor'), at: fmt.dateTime(invoice.createdAt) })}</Typography>
        <Typography variant="body2">{t('detail.statedNet', { amount: fmt.cents(invoice.netTotalCents) })}</Typography>
        <Typography variant="body2">{t('detail.statedVat', { amount: fmt.cents(invoice.vatTotalCents) })}</Typography>
        <Typography variant="body2">{t('detail.statedGross', { amount: fmt.cents(invoice.grossTotalCents) })}</Typography>
        <Typography variant="body2">
          {invoice.shipping.netCents === 0
            ? t('detail.noShipping')
            : t('detail.shipping', {
                amount: fmt.cents(invoice.shipping.netCents),
                vat: invoice.shipping.vatPercent === null ? t('detail.vatUnknown') : fmt.percent(invoice.shipping.vatPercent),
              })}
        </Typography>
        {invoice.acceptedAt !== null && (
          <Typography variant="body2">
            {t('detail.acceptedBy', { name: invoice.acceptedBy?.name || t('audit.unknownActor'), at: fmt.dateTime(invoice.acceptedAt) })}
          </Typography>
        )}
        {invoice.acceptReason && <Typography variant="body2">{t('detail.acceptReason', { reason: invoice.acceptReason })}</Typography>}
      </Box>
    </Box>
  );
}

function LiveReview({ review, poLines, status }: { review: InvoiceReview | null; poLines: Parameters<typeof EvaluationView_>[0]['poLines']; status: string }) {
  const { t } = useTranslation(NS);
  if (review === null) return <Alert severity="info">{status === 'rejected' ? t('detail.noReviewRejected') : t('detail.noReview')}</Alert>;
  if (!review.ok) {
    return (
      <Alert severity="error" data-testid="review-violations">
        <AlertTitle>{t('evaluation.notEvaluable')}</AlertTitle>
        <ViolationList violations={review.violations} />
      </Alert>
    );
  }
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary" data-testid="evidence-hash">
        {t('detail.evidence', { revision: review.revision, hash: review.evidenceHash.slice(0, 12) })}
      </Typography>
      <EvaluationView_ evaluation={{ ok: true, result: review.result }} poLines={poLines} />
    </Stack>
  );
}

function AuditTable({ data }: { data: InvoiceDetailResponse }) {
  const { t } = useTranslation(NS);
  const fmt = useFormatters();
  if (data.events.length === 0) return <Typography variant="body2">{t('audit.none')}</Typography>;
  return (
    <TableContainer>
      <Table size="small" aria-label={t('audit.aria')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('audit.columns.revision')}</TableCell>
            <TableCell>{t('audit.columns.event')}</TableCell>
            <TableCell>{t('audit.columns.statuses')}</TableCell>
            <TableCell>{t('audit.columns.actor')}</TableCell>
            <TableCell>{t('audit.columns.when')}</TableCell>
            <TableCell>{t('audit.columns.reason')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.events.map((event) => (
            <TableRow key={event.id} data-testid={`event-${String(event.revision)}`}>
              <TableCell>{event.revision}</TableCell>
              <TableCell>{t(`audit.event.${event.eventType}`)}</TableCell>
              <TableCell>
                {event.fromStatus === null ? t(`status.${event.toStatus}`) : `${t(`status.${event.fromStatus}`)} → ${t(`status.${event.toStatus}`)}`}
              </TableCell>
              <TableCell>{event.actor.name || t('audit.unknownActor')}</TableCell>
              <TableCell>{fmt.dateTime(event.effectiveAt)}</TableCell>
              <TableCell>{event.reason ?? ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DecisionDialog({
  kind,
  captured,
  live,
  review,
  pending,
  retryRefusal,
  invoiceNumber,
  onClose,
  onSubmit,
}: {
  kind: Decision;
  captured: Captured;
  live: Captured;
  review: InvoiceReview | null;
  /** A held request of this document still exists (its fate is unknown). */
  pending: boolean;
  /** The server refused the explicit retry of the held request, which released it. */
  retryRefusal: ErrorText | null;
  invoiceNumber: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<OperationResult>;
}) {
  const { t } = useTranslation(NS);
  const { t: tc } = useTranslation('common');
  const errorText = useErrorText();
  const stillShown = useStillShown();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<{ error: ErrorText; refused: boolean; reason: string | null } | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);

  const trimmed = reason.trim();
  // What the user decides on must still be what is on screen: a changed revision or review ends the decision here.
  const outdated = live.revision !== captured.revision || live.evidenceHash !== captured.evidenceHash;
  const reasonProblem = decisionReasonError(reason);
  // An unresolved attempt is retried from the pending notice with its own key, never by sending this form again. Once the held
  // request is gone (stored or refused on retry) there is nothing unresolved left.
  const unresolved = failure !== null && !failure.refused && pending;
  const ready = reasonProblem === null && !outdated && !sending && !unresolved && (kind === 'reject' || confirmed);

  const send = async () => {
    if (decisionReasonError(reason) !== null) return;
    setSending(true);
    setFailure(null);
    setBlocked(null);
    const result = await onSubmit(trimmed);
    if (!stillShown()) return;
    setSending(false);
    if (result.status === 'refused') setFailure({ error: errorText(result.error), refused: true, reason: errorReason(result.error) });
    else if (result.status === 'uncertain') setFailure({ error: errorText(result.error), refused: false, reason: null });
    else if (result.status === 'blocked') setBlocked(result.reason);
  };

  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="sm" fullWidth mobileTitle={t(`decision.${kind}Title`)} aria-labelledby="supplier-invoice-decision-title">
      <DialogTitle id="supplier-invoice-decision-title">{t(`decision.${kind}Title`, { number: invoiceNumber })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">{t(`decision.${kind}Intro`)}</Typography>
          {kind === 'accept' && review !== null && review.ok && (
            <Alert severity="warning">
              {review.result.vatUnknown ? t('decision.acceptUnknownVat') : t('decision.acceptVariance')}
            </Alert>
          )}
          {kind === 'reject' && <Alert severity="warning">{t('decision.rejectConsequences')}</Alert>}
          <Typography variant="caption" color="text.secondary">
            {t('decision.quoting', { revision: captured.revision, hash: kind === 'accept' ? captured.evidenceHash.slice(0, 12) : '–' })}
          </Typography>
          <TextField
            label={t('decision.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline
            minRows={2}
            required
            disabled={sending}
            error={reasonProblem === 'tooLong' || reasonProblem === 'controlCharacters'}
            helperText={reasonProblem === 'tooLong' ? t('errors.field.tooLong') : reasonProblem === 'controlCharacters' ? t('decision.reasonControl') : t('decision.reasonHint')}
          />
          {kind === 'accept' && (
            <FormControlLabel
              control={<Checkbox checked={confirmed} disabled={sending} onChange={(event) => setConfirmed(event.target.checked)} />}
              label={t('decision.overrideConfirm')}
            />
          )}
          {outdated && (
            <Alert severity="warning" data-testid="decision-outdated">
              {t('decision.outdated')}
            </Alert>
          )}
          {blocked && <Alert severity="error">{t(`blocked.${blocked}`)}</Alert>}
          {retryRefusal && !pending && (
            <Alert severity="error" data-testid="decision-retry-refused">
              <AlertTitle>{t('decision.retryRefusedTitle')}</AlertTitle>
              {retryRefusal.message}
              {retryRefusal.violations.length > 0 && <ViolationList violations={retryRefusal.violations} />}
              <Typography variant="body2">{t('decision.retryRefusedBody')}</Typography>
            </Alert>
          )}
          {failure && (failure.refused || pending) && (
            <Alert severity={failure.refused ? 'error' : 'warning'} data-testid="decision-error">
              <AlertTitle>{failure.refused ? t('decision.refusedTitle') : t('decision.uncertainTitle')}</AlertTitle>
              {failure.error.message}
              {failure.error.violations.length > 0 && <ViolationList violations={failure.error.violations} />}
              {!failure.refused && <Typography variant="body2">{t('decision.uncertainBody')}</Typography>}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" color={kind === 'accept' ? 'warning' : 'error'} disabled={!ready} onClick={() => void send()}>
          {sending ? tc('loading') : t(`decision.${kind}Confirm`)}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
